import React, { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const MediaMetadataIndicator = ({ taskId, fileIndex, isCustomer, showToast }) => {
 const [metadata, setMetadata] = useState(null);
 const [loading, setLoading] = useState(false);
 const [showDetails, setShowDetails] = useState(false);

 const fetchMetadata = async () => {
 if (loading) return;
 // Если данные уже загружены — просто переключаем видимость
 if (metadata) {
 setShowDetails(prev => !prev);
 return;
 }
 setLoading(true);
 try {
 const API = import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
 const response = await axios.get(`${API}/api/tasks/${taskId}/media-metadata`);
 const fileMetadata = response.data.media_metadata.find(m =>m.file_index === fileIndex);
 setMetadata(fileMetadata || { error: "Metadata not found" });
 setShowDetails(true);
 } catch (error) {
 console.error('Error fetching metadata:', error);
 setMetadata({ error: "Failed to load metadata" });
 setShowDetails(true);
 } finally {
 setLoading(false);
 }
 };

 // Get authenticity badge
 const getAuthenticityBadge = (level, score) => {
 switch (level) {
 case 'high':
 return { icon: '', text: `Высокая (${score}/100)`, color: 'text-green-700 bg-green-100' };
 case 'medium':
 return { icon: '', text: `Средняя (${score}/100)`, color: 'text-yellow-700 bg-yellow-100' };
 case 'low':
 return { icon: '', text: `Низкая (${score}/100)`, color: 'text-red-700 bg-red-100' };
 case 'error':
 return { icon: '', text: 'Ошибка анализа', color: 'text-gray-700 bg-gray-100' };
 default:
 return { icon: '', text: 'Неизвестно', color: 'text-gray-700 bg-gray-100' };
 }
 };

 return (
 <div className="mt-2">
 <button
 onClick={fetchMetadata}
 className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded hover:bg-blue-100 transition-colors"
 >
 {loading ? '' : showDetails ? ' Скрыть' : ' Метаданные'}
 </button>
 
 {metadata && showDetails && !loading && (
 <div className="mt-2 bg-gray-50 rounded-md p-3 text-xs border">
 {metadata.error ? (
 <div className="text-red-600">{metadata.error}</div>
 ) : (
 <div className="space-y-2">
 {/* Authenticity score */}
 <div className="flex items-center justify-between">
 <span className="font-medium">Подлинность:</span>
 <span className={`px-2 py-1 rounded text-xs font-semibold ${getAuthenticityBadge(metadata.authenticity_level, metadata.authenticity_score).color}`}>
 {getAuthenticityBadge(metadata.authenticity_level, metadata.authenticity_score).icon} 
 {getAuthenticityBadge(metadata.authenticity_level, metadata.authenticity_score).text}
 </span>
 </div>
 
 {/* GPS indicator - enhanced to show coordinates */}
 <div className="flex items-start justify-between">
 <span>GPS координаты:</span>
 <div className="text-right">
 {metadata.has_gps ? (
 <div className="space-y-1">
 <div className={`px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700`}>Есть
 </div>
 {/* Show coordinates for customers who pay for the content */}
 {metadata.gps_coordinates?.coordinates_display && (
 <div className="text-xs text-gray-600 font-mono">
 {metadata.gps_coordinates.coordinates_display}
 </div>
 )}
 {/* Link to open in maps */}
 {metadata.gps_coordinates?.decimal_latitude && metadata.gps_coordinates?.decimal_longitude && (
 <a
 href={`https://yandex.ru/maps/?pt=${metadata.gps_coordinates.decimal_longitude},${metadata.gps_coordinates.decimal_latitude}&z=16`}
 target="_blank"
 rel="noopener noreferrer"
 className="text-blue-600 hover:text-blue-800 text-xs underline block"
 >Открыть на карте
 </a>
 )}
 </div>
 ) : (
 <span className={`px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700`}>Нет
 </span>
 )}
 </div>
 </div>
 
 {/* Timestamp indicator */}
 <div className="flex items-center justify-between">
 <span>Дата съемки:</span>
 <span className={`px-2 py-1 rounded text-xs font-medium ${metadata.has_timestamp ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'}`}>
 {metadata.has_timestamp ? ' Есть' : ' Нет'}
 </span>
 </div>
 
 {/* Detailed info - always visible for better UX */}
 {(metadata.camera_info || metadata.capture_date) && (
 <div className="mt-3 pt-2 border-t border-gray-200 space-y-1 text-xs text-gray-700">
 {metadata.camera_info && (
 <div className="flex items-center gap-1">
 <span>Камера: {metadata.camera_info.make || ''} {metadata.camera_info.model || ''}</span>
 </div>
 )}
 {metadata.capture_date && (
 <div className="flex items-center gap-1">
 <span>Дата: {metadata.capture_date}</span>
 </div>
 )}
 {metadata.image_size && (
 <div className="flex items-center gap-1">
 <span>Размер: {metadata.image_size.width}×{metadata.image_size.height}</span>
 </div>
 )}
 </div>
 )}
 
 {/* Warnings */}
 {metadata.warnings && metadata.warnings.length > 0 && (
 <div className="mt-3 pt-2 border-t border-orange-200 space-y-1">
 <div className="font-semibold text-orange-700 text-xs">Рекомендации:</div>
 {metadata.warnings.slice(0, 2).map((warning, idx) => ( // Limit to 2 warnings for space
 <div key={idx} className="text-orange-600 text-xs leading-tight">
 {warning}
 </div>
 ))}
 </div>
 )}
 </div>
 )}
 </div>
 )}
 </div>
 );
};
